/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Vue must initialize before any plugin module that transitively imports vue-router
import axios from 'axios';
import { createApp } from 'vue';

// Plugins
import { registerPlugins } from '@/plugins';

// Components
import App from './App.vue';

import './assets/global.css';
import './assets/typography.css';
import './assets/av-lock-package-font-bridges.css';
import { startActionContrastRootsObserver } from './utils/colorContrast';
import { startCreditCardVuetifyOverridesObserver } from './utils/creditCardVuetifyOverridesSync';
import { startPaymentMethodDialogAttachObserver } from './utils/paymentMethodDialogAttach';
import { declareLockOwnsSentry } from './utils/hostEmbed';
import { LocalAuthHandler } from './utils/LocalAuthHandler';
import {
  getAuthenticatedUserId,
  isUserTokenApiPaths,
} from './utils/authClaims';
import { isMerchantCustomersPath, sanitizeMerchantId } from './utils/helperMethods';
import { LoggerService } from './utils/LoggerService';
import { EnvironmentService } from './services/EnvironmentService';
import { useMerchantStore } from '@authvia-v3/pinia-stores-user';
import { useMerchantBrandingStore } from '@/store/merchantBrandingStore';

declareLockOwnsSentry();

/** Same-origin host profiles (legacy SSRF-safe pattern). */
const profileFetch = axios.create({ baseURL: window.location.origin });

/** Product API host — fixed baseURL + relative paths (same pattern as TokenService / prior Lock SSRF fixes). */
const apiFetch = axios.create({
  baseURL: `https://${EnvironmentService.getAPIHost()}`,
});

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment: string | undefined = token.split('.')[1];
    if (!segment) {
      return null;
    }
    return JSON.parse(atob(segment)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

async function fetchMerchantProfile(): Promise<any | null> {
  const metaMerchantId: string | null | undefined = document
    .querySelector('meta[name="avid"]')
    ?.getAttribute('content');
  const merchantMatch: RegExpMatchArray | null = window.location.pathname.match(/^\/merchants\/([^/]+)/);
  const merchantId: string | null = sanitizeMerchantId(
    metaMerchantId || merchantMatch?.[1],
  );

  if (!merchantId) {
    return null;
  }

  try {
    const token: string | undefined = LocalAuthHandler.getToken();
    if (token) {
      const payload: Record<string, unknown> | null = parseJwtPayload(token);
      const userId: string = getAuthenticatedUserId(payload);
      const safeUserId: string | null = sanitizeMerchantId(userId);
      if (payload && isUserTokenApiPaths(payload) && safeUserId) {
        try {
          const { data } = await apiFetch.get(
            `/users/${safeUserId}/accounts/${merchantId}/profiles`,
            {
              headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${token}`,
              },
            },
          );
          return data;
        } catch {
          // Fall through to host /profiles when USER API is unavailable.
        }
      }
    }

    const { data } = await profileFetch.get(`/profiles/${merchantId}`, {
      headers: { Accept: 'application/json' },
    });
    return data;
  } catch {
    return null;
  }
}

/**
 * Apply merchant profile color to loading animation
 * @param merchantProfile - The merchant profile with color information
 */
function applyMerchantColorToAnimation(merchantProfile: any): void {
  // Get color from profile.profile.theme.primaryColor
  const color = merchantProfile?.profile?.theme?.primaryColor || '#4CAF50';
  
  if (color) {
    // Apply color to CSS variable --color-loader
    document.documentElement.style.setProperty('--color-loader', color);
  }
}

// Create an async initialization function instead of using top-level await
async function initializeApp() {
  const logger = new LoggerService('[main.ts]');
  
  // Step 1: Early capture of merchantId from URL and fetch profile (loading animation continues)
  let merchantProfile: any = null;
  try {
    // Fetch merchant profile (host /profiles or USER API) while loading animation continues
    merchantProfile = await fetchMerchantProfile();
    if (merchantProfile) {
      logger.debug('[main.ts] Fetched merchant profile');

      // Apply merchant profile color to loading animation
      applyMerchantColorToAnimation(merchantProfile);

      // Let animation continue with merchant color for 2 seconds
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  } catch (error) {
    logger.error('[main.ts] Error detecting/fetching merchant:', error);
  }
  
  // Step 2: Continue with other initialization (address, auth, etc.)
  // Early capture of address from any incoming URL before any redirects
  try {
    const rawAddressMatch: RegExpMatchArray | null = window.location.search.match(/[?&]address=([^&]*)/);
    const addressParam: string | undefined = rawAddressMatch
      ? decodeURIComponent(rawAddressMatch[1])
      : undefined;
      
    if (addressParam) {
      LocalAuthHandler.setAddressFromQuery(addressParam);
    }
  } catch {
    logger.error('Failed to capture address from URL');
  }
  
  const { AuthenticationService } = await import('./services/AuthenticationService');
  const authService = new AuthenticationService();
  await authService.handleAuthentication();
  
  // Step 3: Mount Vue app (this will replace the loading screen content)
  // When Vue mounts, it replaces the entire #app innerHTML, so loading elements are automatically removed
  const app = createApp(App);
  await registerPlugins(app);
  app.mount('#app');
  startPaymentMethodDialogAttachObserver();
  startCreditCardVuetifyOverridesObserver();
  startActionContrastRootsObserver();

  // Step 4: Set merchant profile to store (Pinia is now active after app.mount)
  if (merchantProfile) {
    const merchantStore = useMerchantStore();
    const merchantBranding = useMerchantBrandingStore();
    merchantStore.setActiveMerchant(merchantProfile);
    merchantBranding.change(merchantProfile);
    logger.debug('[main.ts] Set merchant profile to store');
  }
  const initialRoutePath:string = LocalAuthHandler.getInitialPath() || '';
  logger.debug('PATH: initializeApp()', initialRoutePath);
  if (initialRoutePath) {
    const { default: router } = await import('./router');
    if (isMerchantCustomersPath(initialRoutePath) && location.search) {
      const queryParams:Record<string, string> = Object.fromEntries(
        new URLSearchParams(location.search)
      );
      router.push({ path: initialRoutePath, query: queryParams });
    } else {
      router.push({ path: initialRoutePath });
    }
  }
}

// Call the initialization function
initializeApp().catch(error => {
  console.error('Failed to initialize application:', error);
});
